import type {
  AuthenticatedUser,
  AuthenticatedUserDto,
} from '@src/features/users';
import { jobTitleFromDto } from '@src/features/users/lib/jobtitle';
import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import AuthenticationContext from './AuthenticationContext';
import type { AuthData, JwtProviderParams } from './providers';
import { JwtAuthProvider } from './providers';
import { useJwtStorage } from './storages';

function createAuthProvider(params: JwtProviderParams) {
  return new JwtAuthProvider(params);
}

export default function AuthenticationProvider({
  children,
}: PropsWithChildren<object>) {
  const storage = useJwtStorage();
  const provider = useRef(
    createAuthProvider({ storage, credentials: storage.credentials }),
  );

  const login = useCallback(
    (username: string, password: string) =>
      provider.current.login(username, password),
    [],
  );
  const verifyTwoFactor = useCallback(
    (challengeToken: string | null, otpCode: string) =>
      provider.current.verifyTwoFactor(challengeToken, otpCode),
    [],
  );
  const startTwoFactorEnrollment = useCallback(
    (token: string) => provider.current.startTwoFactorEnrollment(token),
    [],
  );

  const logout = useCallback(() => provider.current.logout(), []);
  const verifyToken = useCallback(() => provider.current.verifyToken(), []);
  const refreshToken = useCallback(
    (loginAsToken?: string) => provider.current.refreshToken(loginAsToken),
    [],
  );
  const authenticate = useCallback(
    (data: AuthData) => provider.current.authenticate(data),
    [],
  );
  const unauthenticate = useCallback(
    () => provider.current.unauthenticate(),
    [],
  );

  const contextValue = useMemo(
    () => ({
      login,
      logout,
      verifyToken,
      refreshToken,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      authenticate,
      unauthenticate,
      credentials: storage.credentials,
      isAuthenticated: storage.isAuthenticated,
      user: authenticatedUserFromDto(storage.credentials?.user),
    }),
    [
      authenticate,
      login,
      logout,
      refreshToken,
      verifyTwoFactor,
      startTwoFactorEnrollment,
      storage.credentials,
      storage.isAuthenticated,
      unauthenticate,
      verifyToken,
    ],
  );

  return (
    <AuthenticationContext value={contextValue}>
      {children}
    </AuthenticationContext>
  );
}
AuthenticationProvider.displayName = 'AuthenticationProvider';

function authenticatedUserFromDto(
  user: AuthenticatedUserDto,
): AuthenticatedUser | null {
  if (user) {
    return {
      id: user.id,
      email: user.email,
      name: user.name,
      firstName: user.user_first_name,
      familyName: user.user_family_name,
      language: user.language,
      isAdmin: user.is_admin,
      managerId: user.user_user_manager,
      hrId: user.user_user_hr,
      portrait: user.user_portrait,
      jobTitle: user.user_job_title
        ? jobTitleFromDto(user.user_job_title)
        : null,
    };
  }
  return null;
}
